import { alwaysShownPermitDeliveryFieldNames } from '@contracts/order/fields/DeliveryAndNotificationInformationFields';
import { getFieldKeysInCategory } from '@src/orders/domain/Order';
import type { PermitInstruction } from '@src/orders/domain/PermitInstruction';
import { fields } from '@src/orders/schemas/EditOrderFormInputs';
import {
  DriverIdField,
  driverInformationFields,
} from '@src/permitField/DriverInformationFields';
import {
  shouldDisplayTractorAxlesCount,
  TractorAxleCountField,
  TractorIdField,
} from '@src/permitField/TractorInformationFields';
import {
  shouldDisplayTrailerAxlesCount,
  TrailerAxleCountField,
  TrailerIdField,
} from '@src/permitField/TrailerInformationFields';

export type CheckedFields = string[];

export class FormFields {
  private _fields: string[];

  get fields() {
    return this._fields;
  }

  private constructor(fields: string[]) {
    this._fields = fields;
  }

  public filter(otherFormFields: FormFields) {
    this._fields = this.fields.filter(
      (field) => !otherFormFields.fields.includes(field),
    );
  }

  public applyEnforceDeliveryTypeFields() {
    const fieldsSet = new Set(this._fields);
    enforceDeliveryTypeFields(fieldsSet);
    this._fields = Array.from(fieldsSet);
  }

  static convertToFormFields({
    checkedFields,
  }: {
    checkedFields: CheckedFields;
  }) {
    const fields = new Set(checkedFields);

    if (hasTractorField([...checkedFields])) {
      fields.add(TractorIdField.fieldName);
    }

    if (hasTrailerField([...checkedFields])) {
      fields.add(TrailerIdField.fieldName);
    }

    if (hasDriverField([...checkedFields])) {
      fields.add(DriverIdField.fieldName);
    }

    if (!shouldDisplayTractorAxlesCount([...checkedFields])) {
      fields.delete(TractorAxleCountField.fieldName);
    }

    if (!shouldDisplayTrailerAxlesCount([...checkedFields])) {
      fields.delete(TrailerAxleCountField.fieldName);
    }

    enforceDeliveryTypeFields(fields);
    return new FormFields(Array.from(fields));
  }
}

export const getSectionFields = (
  categoryName: string,
  sectionName: string,
  formFields: CheckedFields,
): string[] => {
  const category = fields[categoryName];
  if (!category) {
    return [];
  }

  const sectionFields = category[sectionName];
  if (!sectionFields) {
    return [];
  }

  const sectionFieldKeys = new Set(Object.keys(sectionFields));

  const foundFields = new Set<string>();

  formFields.forEach((field) => {
    if (sectionFieldKeys.has(field)) {
      foundFields.add(field);
    }
  });

  if (
    sectionName === 'tractorInformation' &&
    formFields.includes(TractorIdField.fieldName)
  ) {
    foundFields.add(TractorIdField.fieldName);
  }

  if (
    sectionName === 'tractorInformation' &&
    !shouldDisplayTractorAxlesCount(formFields)
  ) {
    foundFields.delete(TractorAxleCountField.fieldName);
  }

  if (
    sectionName === 'trailers' &&
    !shouldDisplayTrailerAxlesCount(formFields)
  ) {
    foundFields.delete(TrailerAxleCountField.fieldName);
  }

  return Array.from(foundFields);
};

const hasTractorField = (checkedFields: CheckedFields) => {
  return checkedFields.some(
    (item) =>
      item.toLowerCase().includes('tractor') ||
      item === 'vehicleOfSpecialDesign',
  );
};

const hasTrailerField = (checkedFields: CheckedFields) => {
  const trailerFields = getSectionFields(
    'haulingEquipment',
    'trailers',
    checkedFields,
  );
  return checkedFields.some((field) => trailerFields.includes(field));
};

const hasDriverField = (checkedFields: CheckedFields) => {
  return checkedFields.some((field) =>
    Object.keys(driverInformationFields).includes(field),
  );
};

export const getCategoryFields = (
  categoryName: string,
  checkedFields: CheckedFields,
): string[] => {
  const category = fields[categoryName];
  if (!category) {
    return [];
  }

  const categoryFields = getFieldKeysInCategory(categoryName);
  const foundFields = new Set<string>();
  checkedFields.forEach((field) => {
    if (categoryFields.includes(field)) {
      foundFields.add(field);
    }
  });

  return Array.from(foundFields);
};

export const hasCategory = (
  categoryName: string,
  checkedFields: CheckedFields,
  permitInstructions: PermitInstruction[],
): boolean => {
  if (categoryName === 'instructions') {
    return permitInstructions.length > 0;
  }
  return getCategoryFields(categoryName, checkedFields).length > 0;
};

/**
 * @description
 * Older permit types that were initially imported in the application and were never updated since,
 * didn't have the Delivery Types fields in their field list.
 *
 * Therefore, the permit applications created from those permit types don't have those fields in their list either,
 * which means those are disabled in the form.
 *
 * This function adds those fields in the field list if not there,
 * in order to fix those permits as soon they enter the form and prevent the problem when those are disabled.
 * **/
const enforceDeliveryTypeFields = (checkedFields: Set<string>) => {
  alwaysShownPermitDeliveryFieldNames.forEach((fieldName) =>
    enforceField(fieldName, checkedFields),
  );
};

const enforceField = (fieldName: string, checkedFields: Set<string>) => {
  if (!checkedFields.has(fieldName)) {
    checkedFields.add(fieldName);
  }
};
